import React, { useEffect, useState } from "react";
import { Check, Star, Wallet, Bitcoin, CreditCard, Zap, Sparkles } from "lucide-react";
import { plans, trialOffer } from "../mock/mock";
import { fetchPlansConfig } from "../lib/api";

// Compute status text. Rules:
//  - If server override exists and is a string, use that value verbatim (empty string = show nothing).
//  - If no override exists yet (undefined), fall back to "Available".
const computeStatus = (override) =>
  override && typeof override.status === "string" ? override.status : "Available";

const computePrice = (base, override) =>
  override && typeof override.price === "number" && !isNaN(override.price)
    ? override.price
    : base;

const StatusPill = ({ text }) => {
  if (!text) return null;
  const low = text.toLowerCase();
  const negative = low.includes("not");
  const color = negative
    ? "bg-slate-100 text-slate-500 border-slate-200"
    : "bg-emerald-50 text-emerald-700 border-emerald-200";
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold border ${color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${negative ? "bg-slate-400" : "bg-emerald-500"}`} />
      {text}
    </span>
  );
};

const Membership = () => {
  const [cfg, setCfg] = useState({});

  const load = async () => {
    try {
      const c = await fetchPlansConfig();
      setCfg(c || {});
    } catch {}
  };

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("ep-data-updated", h);
    const iv = setInterval(load, 20000);
    return () => {
      window.removeEventListener("ep-data-updated", h);
      clearInterval(iv);
    };
  }, []);

  const trialPrice = computePrice(trialOffer.price, cfg.trial);
  const trialStatus = computeStatus(cfg.trial);

  return (
    <div>
      <div className="bg-ep-royal py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 text-center">
          <div className="text-[#ff5a5a] font-semibold tracking-widest text-xs uppercase">Membership</div>
          <h1 className="text-white text-4xl sm:text-5xl font-bold mt-3">Choose the plan that fits you</h1>
          <p className="mt-4 text-white/70 max-w-2xl mx-auto">
            All plans include research delivered to your inbox at least 12–24
            hours before kick-off, plus full access to the public results
            archive.
          </p>
        </div>
      </div>

      <section className="bg-[#f5f7ff] py-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 mb-10">
            <h3 className="text-[#0a1a4a] text-xl font-bold">Good to know</h3>
            <div className="grid md:grid-cols-2 gap-3 mt-4 text-sm text-[#0a1a4a]/75">
              <Bullet>Immediate access confirmation after subscription.</Bullet>
              <Bullet>Picks are sent directly to your registered email.</Bullet>
              <Bullet>Picks arrive at least 12–24 hours before kick-off.</Bullet>
              <Bullet>You will receive between 1–2 picks per day.</Bullet>
              <Bullet>Covers almost all leagues where value can be found.</Bullet>
              <Bullet>Use whichever regulated bookmaker you prefer.</Bullet>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Horizontal trial offer spanning all 3 columns */}
            <div className="lg:col-span-3 relative rounded-2xl overflow-hidden bg-gradient-to-r from-[#0a1a4a] via-[#1c3aa8] to-[#0a1a4a] p-6 sm:p-8 card-lift">
              <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-[#ff5a5a]/20 blur-2xl" />
              <div className="relative flex flex-col md:flex-row items-start md:items-center gap-5 md:gap-8">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-xl bg-[#ff5a5a] flex items-center justify-center flex-shrink-0 shadow-lg">
                    <Zap size={26} className="text-white" strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-[#ff5a5a]/20 text-[#ff9a9a] text-[10px] font-bold uppercase tracking-widest">
                      <Sparkles size={11} /> Trial offer
                    </div>
                    <div className="text-white text-2xl sm:text-3xl font-extrabold mt-1 tracking-tight">
                      {trialOffer.name}
                    </div>
                    <div className="text-white/70 text-sm mt-0.5">{trialOffer.tagline}</div>
                    {trialStatus && (
                      <div className="mt-2">
                        <StatusPill text={trialStatus} />
                      </div>
                    )}
                  </div>
                </div>

                <div className="hidden md:block h-16 w-px bg-white/20" />

                <div className="flex-1 flex flex-wrap gap-x-6 gap-y-2 text-white/85 text-sm">
                  {trialOffer.features.map((f, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Check size={14} className="text-[#ff5a5a]" strokeWidth={3} />
                      <span>{f}</span>
                    </div>
                  ))}
                </div>

                <div className="md:text-right md:ml-auto">
                  <div className="flex items-baseline gap-1">
                    <span className="text-white text-4xl sm:text-5xl font-extrabold">
                      {trialOffer.currency}{trialPrice}
                    </span>
                    <span className="text-white/60 text-sm">/ {trialOffer.period}</span>
                  </div>
                </div>
              </div>
            </div>

            {plans.map((p) => {
              const override = cfg[p.id];
              const price = computePrice(p.price, override);
              const status = computeStatus(override);
              return (
              <div
                key={p.id}
                className={`relative rounded-2xl p-8 card-lift bg-white ${
                  p.highlighted
                    ? "ring-2 ring-[#ff5a5a] shadow-2xl"
                    : "border border-slate-200"
                }`}
              >
                {p.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#ff5a5a] text-white text-xs font-bold px-4 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
                    <Star size={12} className="fill-white" />
                    {p.badge}
                  </div>
                )}
                <h3 className="text-xl font-bold text-[#0a1a4a]">{p.name}</h3>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-5xl font-extrabold text-[#0a1a4a]">
                    {p.currency}{price}
                  </span>
                  <span className="text-[#0a1a4a]/60 text-sm">/ {p.period}</span>
                </div>
                {status && (
                  <div className="mt-3">
                    <StatusPill text={status} />
                  </div>
                )}
                <ul className="mt-6 space-y-3">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-[#0a1a4a]/80">
                      <div className="w-5 h-5 rounded-full bg-[#ff5a5a]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check size={12} className="text-[#ff5a5a]" strokeWidth={3} />
                      </div>
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 pt-5 border-t border-slate-100 text-xs text-[#0a1a4a]/50 text-center">
                  Contact us to activate this plan
                </div>
              </div>
              );
            })}
          </div>

          <div className="mt-16">
            <h3 className="text-center text-[#0a1a4a] text-xl font-bold">Accepted payment methods</h3>
            <p className="text-center text-[#0a1a4a]/60 text-sm mt-2 mb-6">
              Available payment options for activated plans
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <PayLogo icon={Wallet} label="Skrill" bg="#7A1F8A" />
              <PayLogo icon={CreditCard} label="PayPal" bg="#003087" />
              <PayLogo icon={Bitcoin} label="Crypto" bg="#F7931A" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const Bullet = ({ children }) => (
  <div className="flex items-start gap-2.5">
    <div className="w-5 h-5 rounded-full bg-[#ff5a5a]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
      <Check size={12} className="text-[#ff5a5a]" strokeWidth={3} />
    </div>
    <span>{children}</span>
  </div>
);

const PayLogo = ({ icon: Icon, label, bg }) => (
  <div
    className="flex items-center gap-3 px-6 h-14 rounded-xl text-white font-bold shadow-md"
    style={{ background: bg }}
  >
    <Icon size={22} />
    <span className="tracking-wide">{label}</span>
  </div>
);

export default Membership;
